import { formatDate } from "helpers/Helpers";

export type TQuickExportTemplateId =
  | "dateRange"
  | "dateRangeWithRegisterFilters"
  | "dateRangeWithRegisterIdsOnly"
  | "dateRangeWithVendorDeptFilters"
  | "compareSalesByDate"
  | "dateRangeWithRegisterId"
  | "singleDate"
  | "singleDateAgedAr"
  | "serverSummaries"
  | "prizeAccounts"
  | "creditBooks"
  | "accountsWithCustomerTypes"
  | "bookingDeposits"
  | "accountsReceivableZeroBalance"
  | "userPaymentMethods"
  | "staffAccountShifts"
  | "roundsByBookingCategory"
  | "clientDateRange"
  | "clientDetailedSalesFilters"
  | "clientOrderExceptions"
  | "clientAgedAr"
  | "clientPrizeAccounts"
  | "cashReconciliation"
  | "cashReconciliationWorksheet"
  | "formResponses"
  | "clientFormResponses"
  | "clientTeeSheetUtilization"
  | "clientSingleDate";

export function buildTodayDateRange(): string[] {
  const today = new Date();
  return [formatDate(today), `${formatDate(today)} 23:59:59`];
}

export function buildReportTemplateParams(
  templateId: TQuickExportTemplateId,
  reportId: number,
): Record<string, unknown> {
  const dateRange = buildTodayDateRange();
  const today = formatDate(new Date());
  const base = { report_id: reportId };

  switch (templateId) {
    case "dateRange":
      return { ...base, date_range: dateRange };
    case "dateRangeWithRegisterFilters":
      return { ...base, date_range: dateRange, register_ids: [], sales_channels: [] };
    case "dateRangeWithRegisterIdsOnly":
      return { ...base, date_range: dateRange, register_ids: [] };
    case "dateRangeWithVendorDeptFilters":
      return {
        ...base,
        date_range: dateRange,
        vendor_ids: [],
        register_ids: [],
        department_ids: [],
        category_ids: [],
        subcategory_ids: [],
        user_ids: [],
      };
    case "compareSalesByDate":
      return {
        ...base,
        date_range: dateRange,
        vendor_ids: [],
        register_ids: [],
        department_ids: [],
        category_ids: [],
        subcategory_ids: [],
        compare_date_range: dateRange,
      };
    case "dateRangeWithRegisterId":
      return { ...base, date_range: dateRange, register_id: null };
    case "singleDate":
      return { ...base, date: today };
    case "singleDateAgedAr":
      return {
        ...base,
        date: today,
        include_zero_balances: false,
        include_closed_accounts: false,
      };
    case "serverSummaries":
      return {
        ...base,
        date_range: dateRange,
        kitchen_grat: 0,
        bar_grat: 0,
        support_grat: 0,
      };
    case "prizeAccounts":
      return { ...base };
    case "creditBooks":
      return { ...base, customer_type_ids: [] };
    case "accountsWithCustomerTypes":
      return {
        ...base,
        date_range: dateRange,
        customer_types: [],
        include_zero_balances: false,
        include_closed_accounts: false,
      };
    case "bookingDeposits":
      return { ...base, date_range: dateRange, customer_types: [] };
    case "accountsReceivableZeroBalance":
      return { ...base, date: today, zero_balance: false, include_closed_accounts: false };
    case "userPaymentMethods":
      return {
        ...base,
        date_range: dateRange,
        customer_payment_methods: false,
        gift_cards: false,
        rain_checks: false,
        credit_books: false,
        house_accounts: false,
      };
    case "staffAccountShifts":
      return { ...base, date_range: dateRange, user_ids: [] };
    case "roundsByBookingCategory":
      return {
        ...base,
        date_range: dateRange,
        selected_days: [],
        time_range: ["06:00", "20:00"],
      };
    case "clientDateRange":
      return { ...base, date_range: dateRange, facility_ids: [] };
    case "clientDetailedSalesFilters":
      return {
        ...base,
        date_range: dateRange,
        facility_ids: [],
        vendor_ids: [],
        register_ids: [],
        department_ids: [],
        category_ids: [],
        subcategory_ids: [],
      };
    case "clientOrderExceptions":
      return { ...base, date_range: dateRange, facility_ids: [], types: [] };
    case "clientAgedAr":
      return { ...base, date_range: dateRange, includeBalancesUnder0: false };
    case "clientPrizeAccounts":
      return { ...base, facility_ids: [] };
    case "cashReconciliation":
      return {
        ...base,
        date: today,
        date_range: dateRange,
        register_ids: [],
        sales_channels: [],
      };
    case "cashReconciliationWorksheet":
      return { ...base, date: today, register_ids: [] };
    case "formResponses":
      return { ...base, date_range: dateRange, form_id: null };
    case "clientFormResponses":
      return { ...base, date_range: dateRange, facility_ids: [], form_id: null };
    case "clientTeeSheetUtilization":
      return { ...base, date_range: dateRange, facility_id: null };
    case "clientSingleDate":
      return { ...base, date: today };
    default:
      return { ...base, date_range: dateRange };
  }
}
